import pluralize from 'pluralize';
import format from 'date-fns/format';

import { EVENT_ADDON_ITEM_TYPE } from '../../../constants/event-items';
import { capitalize } from '../../../util/text';
import {
  ALBUM_SLIDESHOW_STATUS,
  ALBUM_SUBTYPE,
} from '../../../constants/albums';
import {
  isMainPhotosAlbum,
  isMemoryBook,
  isVideoAlbum,
} from '../../../util/album';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../constants/contributors';

/**
 * Determines if it is possible to upload to a given album
 *
 * @param {Array} albums the albums to check for
 * @param {boolean} authenticated wether or not the user is authenticated
 *
 * @returns {boolean}
 */
export const canUploadToSpecialtyEventAlbums = (albums, authenticated) => {
  if (
    !albums ||
    albums?.length === 0 ||
    albums?.filter(Boolean)?.length === 0
  ) {
    return false;
  }

  return !!albums.find(album => {
    const { auto_contributor_settings: contribSettings } = album ?? {};

    const canUpload =
      (!authenticated && album?.enable_external_upload) ||
      (contribSettings?.upload_access && // Avoids undefined !== null.
        contribSettings?.upload_access !== CONTRIBUTOR_UPLOAD_ACCESS.NONE);

    return canUpload;
  });
};

/**
 * Determines if it is possible to view the given albums
 *
 * @param {Array} albums the albums to check for
 * @param {Object} eventSettings the event settings
 *
 * @returns {boolean}
 */
export const canViewSpecialtyEventAlbums = (albums, eventSettings) => {
  if (
    !albums ||
    albums?.length === 0 ||
    albums?.filter(Boolean)?.length === 0
  ) {
    return false;
  }

  // Photos Album scenario. If there is more than 1, at least 1 can always be seen.
  if (albums?.length > 1) {
    return true;
  }

  // We will now allow viewing the photos regardless of the event date, but so long as there IS an event date
  if (isMainPhotosAlbum(albums[0]) && !new Date(eventSettings?.event_date)) {
    return false;
  }

  if (
    isMemoryBook(albums[0]) &&
    albums[0].slideshow?.status !== ALBUM_SLIDESHOW_STATUS.PUBLISHED
  ) {
    return false;
  }

  if (
    isVideoAlbum(albums[0]) &&
    albums[0].slideshow?.status !== ALBUM_SLIDESHOW_STATUS.PUBLISHED
  ) {
    return false;
  }

  return true;
};

/**
 * Determines whether or not the upload button for a given album must be shown.
 *
 * @param {Array} albums the albums to check
 *
 * @returns {boolean}
 */
export const shouldShowSpecialtyEventUploadButtons = albums => {
  if (!albums) {
    return false;
  }

  return (
    canUploadToSpecialtyEventAlbums(albums) ||
    canViewSpecialtyEventAlbums(albums)
  );
};

/**
 * Determines if a given button should be disabled
 *
 * @param {Object} album the album to check for
 * @param {Object} eventSettings the event settings
 * @param {Boolean} isWaldofiedAlbum whether or not this is the waldofied album
 *
 * @returns {boolean}
 */
export const disableSpecialtyEventUploadButton = (
  album,
  eventSettings,
  isWaldofiedAlbum,
) => {
  if (isWaldofiedAlbum) {
    return new Date() < new Date(eventSettings?.event_date);
  }

  return false;
};

/**
 * Determines if we should assume Live Event Day
 *
 * @param {Object} waldofiedAlbum the waldofied album
 * @param {Object} eventSettings the event settings
 * @param {Boolean} dateOnly whether to only match the dates or rely on the waldofiedAlbum
 *
 * @returns {boolean}
 */
export const isLiveEventDay = (
  waldofiedAlbum,
  eventSettings,
  dateOnly = false,
) => {
  const { event_date: eventDate } = eventSettings ?? {};
  const currentDate = format(new Date(), 'yy-MM-dd');
  const parsedEventDate = eventDate
    ? // Add time zone in order to prevent the wrong date from being parsed
      format(new Date(`${eventDate}T23:59:59Z`), 'yy-MM-dd')
    : '';

  if (dateOnly) {
    return currentDate === parsedEventDate;
  }

  if (!waldofiedAlbum || !eventSettings) {
    return false;
  }

  // If Slideshow is not "Live", then it's not a Live Event Day
  if (waldofiedAlbum?.slideshow?.status !== ALBUM_SLIDESHOW_STATUS.PUBLISHED) {
    return false;
  }

  // "Live" will only be considered as day of the event
  return currentDate === parsedEventDate;
};

/**
 * Retrieves the requested specialty albums
 *
 * @param {Array} albums the albums to search through
 * @param {String} albumSubtype the albums sub type
 * @param {Array} eventAddonTypes the addon types desired
 *
 * @returns {Array}
 */
export const getSpecialtyAlbums = (albums, albumSubtype, eventAddonTypes) => {
  const specialtyAlbums = [];

  if (!albums || (eventAddonTypes?.length ?? 0) === 0) {
    return specialtyAlbums;
  }

  if (eventAddonTypes.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED)) {
    specialtyAlbums.push(albums.find(album => isMainPhotosAlbum(album)));
  }

  if (eventAddonTypes.includes(EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK)) {
    specialtyAlbums.push(albums.find(album => isMemoryBook(album)));
  }

  if (eventAddonTypes.includes(EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO)) {
    specialtyAlbums.push(albums.find(album => isVideoAlbum(album)));
  }

  return specialtyAlbums;
};

/**
 * Retrieves the names of every album passed in a format appropriate for invitations flow
 *
 * @param {Array} albums the albums to get the name for
 *
 * @returns {Array}
 */
export const getSpecialtyAlbumInviteNames = albums => {
  if (!albums || albums.length === 0) {
    return [];
  }

  return albums.map(album => {
    const { album_subtype: subType } = album;
    const isMemoryBookBool = isMemoryBook(album);
    const isGroupVideo = isVideoAlbum(album);
    const vgbSubTypes = [ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS];

    if (isGroupVideo && vgbSubTypes.includes(subType)) {
      return 'Video Guestbook';
    }

    if (isMemoryBookBool) {
      return 'Memories Slideshow';
    }

    if (isGroupVideo) {
      return 'Group Video';
    }

    return `${capitalize(pluralize(subType.toLowerCase(), 1))} Day Pics`;
  });
};

/**
 * Retrieves the event name for a given event type album
 *
 * @param {String} albumSubtype the album subtype to get the event for
 *
 * @returns {String}
 */
export const getGenericSubtypeEventName = albumSubtype => {
  switch (albumSubtype) {
    case ALBUM_SUBTYPE.WEDDINGS:
      return 'Wedding day';
    case ALBUM_SUBTYPE.MEMORIALS:
      return 'Memorial service';
    case ALBUM_SUBTYPE.BIRTHDAYS:
      return 'Birthday party';
    case ALBUM_SUBTYPE.GRADUATIONS:
      return 'Graduation party';
    case ALBUM_SUBTYPE.ANNIVERSARIES:
      return 'Anniversary party';
    case ALBUM_SUBTYPE.VACATIONS:
      return 'Trip';
    case ALBUM_SUBTYPE.CUSTOM_EVENT:
    default:
      return 'Event';
  }
};
